import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Radio, Youtube, Bell } from "lucide-react";
import { api } from "../lib/api";

const YT_CHANNEL = "https://www.youtube.com/@dennishaymon4027";

export default function LiveBroadcast() {
  const [live, setLive] = useState(null);

  const load = () => api.get("/live").then((r) => setLive(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const isLive = live?.is_live && live?.youtube_video_id;
  const domain = typeof window !== "undefined" ? window.location.hostname : "";

  return (
    <section id="live" className="relative py-24 bg-[#07111f] grain overflow-hidden" data-testid="live-broadcast-section">
      <div className="absolute -top-20 left-1/3 h-72 w-72 rounded-full bg-[#a91520]/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-8">
          {isLive ? (
            <span className="inline-flex items-center gap-2 bg-[#a91520] px-3 py-1.5 rounded-full font-cond font-bold uppercase text-sm tracking-wide" data-testid="live-badge">
              <span className="h-2.5 w-2.5 rounded-full bg-white animate-pulse" /> Live Now
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 bg-[#25364d] px-3 py-1.5 rounded-full font-cond font-bold uppercase text-sm tracking-wide text-[#94A3B8]" data-testid="live-badge">
              <Radio className="h-4 w-4" /> Offline
            </span>
          )}
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">Watch <span className="text-[#0066FF] blue-glow">Live</span></h2>
        </div>

        {isLive ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-4"
          >
            <div>
              <div className="relative w-full rounded-2xl overflow-hidden bg-black border border-[#0066FF]/25" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  title="The Other Side of Blue Live"
                  src={`https://www.youtube.com/embed/${live.youtube_video_id}?autoplay=1`}
                  allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                  data-testid="live-video-iframe"
                />
              </div>
              {(live.title || live.blurb) && (
                <div className="mt-4 rounded-xl bg-[#0c1c30] border border-[#0066FF]/15 p-5">
                  <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#70a7ff] mb-1">Now Discussing</div>
                  {live.title && <h3 className="font-cond font-bold uppercase text-2xl leading-tight">{live.title}</h3>}
                  {live.blurb && <p className="text-sm text-[#94A3B8] mt-1">{live.blurb}</p>}
                </div>
              )}
            </div>
            <div className="hidden lg:block rounded-2xl overflow-hidden border border-[#0066FF]/25 bg-[#0c1c30]">
              <iframe
                title="Live Audience Chat"
                src={`https://www.youtube.com/live_chat?v=${live.youtube_video_id}&embed_domain=${domain}`}
                className="w-full h-full min-h-[500px] border-0"
                data-testid="live-chat-iframe"
              />
            </div>
          </motion.div>
        ) : (
          <div className="rounded-2xl border border-[#0066FF]/20 bg-[#0c1c30] p-10 sm:p-14 text-center" data-testid="live-offline-card">
            <div className="flex justify-center mb-4">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0066FF]/15 border border-[#0066FF]/40">
                <Radio className="h-8 w-8 text-[#0066FF]" />
              </span>
            </div>
            <h3 className="font-display text-3xl uppercase">We're Not Live Right Now</h3>
            <p className="mt-3 text-[#94A3B8] max-w-lg mx-auto">
              New episodes stream weekly. Subscribe on YouTube and hit the bell to get notified the moment we go live.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <a href={YT_CHANNEL} target="_blank" rel="noopener noreferrer" data-testid="live-youtube-link" className="flex items-center gap-2 rounded-full bg-[#FF0000] hover:bg-[#e00000] transition-colors px-6 py-3 font-cond font-bold uppercase text-white">
                <Youtube className="h-5 w-5" /> Subscribe on YouTube
              </a>
              <a href="#subscribe" data-testid="live-notify-link" className="flex items-center gap-2 rounded-full border border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white transition-colors px-6 py-3 font-cond font-bold uppercase">
                <Bell className="h-5 w-5" /> Get Notified
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
